import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { inject } from '@angular/core';

/** Wraps the first case-insensitive occurrence of `term` in `text` in a
 *  `<mark class="masterdata-search-highlight">`. The text is HTML-escaped manually
 *  (`DomSanitizer.sanitize` collapses whitespace), so the result is safe to trust. */
@Pipe({ name: 'highlightMatch' })
export class HighlightMatchPipe implements PipeTransform {
  private readonly sanitizer = inject(DomSanitizer);

  transform(text: string | null | undefined, term: string | null | undefined): SafeHtml {
    const value = text ?? '';
    const needle = (term ?? '').trim();
    if (!needle) return value;
    const pattern = new RegExp(escapeRegExp(needle), 'i');
    const match = pattern.exec(value);
    if (!match) return escapeHtml(value);
    const start = match.index;
    const end = start + match[0].length;
    const marked =
      escapeHtml(value.slice(0, start)) +
      `<mark class="masterdata-search-highlight">${escapeHtml(match[0])}</mark>` +
      escapeHtml(value.slice(end));
    return this.sanitizer.bypassSecurityTrustHtml(marked);
  }
}

/** Escape regex metacharacters so the search term matches literally. */
function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Escape the HTML-significant characters so untrusted text renders literally, preserving whitespace. */
function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
